import React, { useState, useEffect, useRef } from 'react';
import { ParticipantRole, ChatMessage } from '../../types/chat';
import { functions, db } from '../../firebase';
import { httpsCallable } from 'firebase/functions';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import { Loader2, AlertCircle } from 'lucide-react';

interface ChatProps {
    therapistId: string;
    therapistName: string;
    otherMemberId: string;
    otherMemberName: string;
    otherMemberRole: ParticipantRole;
    currentUserId: string;
    currentUserName: string;
    currentUserRole: ParticipantRole;
}

const Chat: React.FC<ChatProps> = ({
    therapistId,
    therapistName,
    otherMemberId,
    otherMemberName,
    otherMemberRole,
    currentUserId,
    currentUserName,
    currentUserRole,
}) => {
    const [message, setMessage] = useState('');
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSending, setIsSending] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    // Gera o chatId baseado em therapistId-otherMemberId
    const chatId = `${therapistId}-${otherMemberId}`;
    
    // Determina quem é o destinatário da mensagem
    const receiverId = currentUserId === therapistId ? otherMemberId : therapistId;
    const receiverName = currentUserId === therapistId ? otherMemberName : therapistName;
    const receiverRole = currentUserId === therapistId ? otherMemberRole : ParticipantRole.TERAPEUTA;

    // Scroll para a última mensagem
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // Marca mensagens como lidas quando o chat é aberto
    useEffect(() => {
        const markAsRead = async () => {
            try {
                const markChatAsRead = httpsCallable(functions, 'markChatAsRead');
                await markChatAsRead({
                    therapistId,
                    otherMemberId,
                });
            } catch (err: any) {
                // Ignora erro se o documento do chat não existir ainda (sem mensagens)
                if (!err.message?.includes('NOT_FOUND') && !err.message?.includes('No document to update')) {
                    console.error('Erro ao marcar mensagens como lidas:', err);
                }
            }
        };

        // Só marca como lido se houver mensagens
        if (messages.length > 0) {
            markAsRead();
        }
    }, [therapistId, otherMemberId, messages.length]);

    // Escuta mensagens em tempo real
    useEffect(() => {
        setIsLoading(true);
        setError(null);

        const messagesRef = collection(db, 'chats', chatId, 'messages');
        const messagesQuery = query(messagesRef, orderBy('timestamp', 'asc'));

        const unsubscribe = onSnapshot(
            messagesQuery,
            (snapshot) => {
                const loadedMessages: ChatMessage[] = snapshot.docs.map((doc) => {
                    const data = doc.data();
                    return {
                        id: doc.id,
                        senderId: data.senderId,
                        senderName: data.senderName,
                        senderRole: data.senderRole,
                        receiverId: data.receiverId,
                        receiverName: data.receiverName,
                        receiverRole: data.receiverRole,
                        message: data.message,
                        timestamp: data.timestamp?.toDate() || new Date(),
                        read: data.read,
                    };
                });
                setMessages(loadedMessages);
                setIsLoading(false);
            },
            (err) => {
                console.error('Erro ao carregar mensagens:', err);
                setError('Erro ao carregar mensagens. Tente novamente.');
                setIsLoading(false);
            }
        );

        return () => unsubscribe();
    }, [chatId]);

    const handleSendMessage = async () => {
        if (!message.trim() || isSending) return;

        setIsSending(true);
        setError(null);

        try {
            const sendChatMessage = httpsCallable(functions, 'sendChatMessage');
            await sendChatMessage({
                senderId: currentUserId,
                senderName: currentUserName,
                senderRole: currentUserRole,
                receiverId,
                receiverName,
                receiverRole,
                message: message.trim(),
            });

            setMessage('');
        } catch (err: any) {
            console.error('Erro ao enviar mensagem:', err);
            setError(err.message || 'Erro ao enviar mensagem. Tente novamente.');
        } finally {
            setIsSending(false);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    return (
        <div className="flex flex-col h-full bg-white rounded-lg shadow-lg">
            {/* Header com nome e posição */}
            <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-6 py-4 rounded-t-lg">
                <h2 className="text-lg font-semibold">
                    {receiverRole}: {receiverName}
                </h2>
            </div>

            {/* Área de mensagens */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {isLoading ? (
                    <div className="flex items-center justify-center h-full">
                        <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
                        <span className="ml-2 text-gray-500">Carregando mensagens...</span>
                    </div>
                ) : messages.length === 0 ? (
                    <div className="flex items-center justify-center h-full text-gray-400">
                        <p>Nenhuma mensagem ainda. Comece a conversa!</p>
                    </div>
                ) : (
                    <>
                        {messages.map((msg) => {
                            const isSentByMe = msg.senderId === currentUserId;
                            return (
                                <div key={msg.id} className="flex flex-col">
                                    <div className={`flex ${isSentByMe ? 'justify-end' : 'justify-start'}`}>
                                        <div
                                            className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                                                isSentByMe
                                                    ? 'bg-purple-600 text-white'
                                                    : 'bg-gray-200 text-gray-800'
                                            }`}
                                        >
                                            <p className="text-sm font-semibold mb-1">
                                                {isSentByMe ? 'Você' : msg.senderName}
                                            </p>
                                            <p className="text-sm">{msg.message}</p>
                                        </div>
                                    </div>
                                    <span
                                        className={`text-xs text-gray-500 mt-1 ${
                                            isSentByMe ? 'text-right' : 'text-left'
                                        }`}
                                    >
                                        {msg.timestamp.toLocaleTimeString('pt-BR', {
                                            hour: '2-digit',
                                            minute: '2-digit',
                                        })}
                                    </span>
                                </div>
                            );
                        })}
                        <div ref={messagesEndRef} />
                    </>
                )}
            </div>

            {/* Mensagem de erro */}
            {error && (
                <div className="px-4 py-2 bg-red-50 border-t border-red-200">
                    <div className="flex items-center gap-2 text-red-700">
                        <AlertCircle className="w-4 h-4" />
                        <span className="text-xs">{error}</span>
                    </div>
                </div>
            )}

            {/* Input de mensagem */}
            <div className="border-t border-gray-200 p-4">
                <div className="flex items-end space-x-2">
                    <textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Digite sua mensagem..."
                        className="flex-1 resize-none border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                        rows={2}
                        disabled={isSending}
                    />
                    <button
                        onClick={handleSendMessage}
                        disabled={!message.trim() || isSending}
                        className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors h-full flex items-center gap-2"
                    >
                        {isSending ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Enviando...
                            </>
                        ) : (
                            'Enviar'
                        )}
                    </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                    Pressione Enter para enviar ou Shift+Enter para quebrar linha
                </p>
            </div>
        </div>
    );
};

export default Chat;